import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";

import type { DocStatus } from "@xop/dashboard-shared";

import { listCategories, uploadDoc } from "./api.js";
import { applyBulkPatch, runBulkUpload, type BulkFileState } from "./bulkUpload.js";
import { BulkFileList } from "./bulkUploadUi.js";
import { folderOptions } from "./folderOptions.js";
import { FolderSelect } from "./FolderSelect.js";
import { formatBytes } from "./utils.js";
import { BackButton } from "@/components/ui/back-button";

/** Upload one OR MANY files in a single go. Drag in (or pick) several files and
 * each becomes its own Document under the chosen category — the same fan-out the
 * Bulk-upload page does, now on the primary Upload screen. A single file keeps
 * the editable Title and redirects to the library on success; multiple files
 * show per-file progress and a summary (title defaults to each file name).
 *
 * The dashboard server forwards each multipart body to the support-center,
 * which lands the file in DigitalOcean Spaces (`xop-storage`) via Active Storage
 * and queues the AI gateway to ingest it into pgvector. */
export function DocumentUploadPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<BulkFileState[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState<DocStatus>("published");
  // Pre-filled by "+ Add to this folder" (the param rides through the mode
  // chooser). A file is filed into ONE folder or sub-folder and nothing
  // else: there is no release picker on this page (2026-09-23). Release
  // documentation is added from the version itself on CRM ▸ Releases; a
  // `default_release_id` still arriving from a release page's "+ Add to
  // this version" link is honoured, but it is never offered as a choice.
  const [searchParams] = useSearchParams();
  const [categoryId, setCategoryId] = useState(() => searchParams.get("category_id") ?? "");
  const releaseId = searchParams.get("default_release_id") ?? "";
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<{ done: number; failed: number } | null>(null);

  const { data: cats } = useQuery({ queryKey: ["docs", "categories"], queryFn: listCategories });
  const folders = useMemo(() => folderOptions(cats?.data ?? []), [cats]);
  const categoryName = useMemo(
    () => folders.find((f) => String(f.id) === categoryId)?.label ?? null,
    [folders, categoryId],
  );

  const single = files.length === 1;
  const multi = files.length > 1;

  /** Append picked files, de-duped by name+size (within the batch AND against
   * what's already listed) so re-dropping the same file doesn't double up.
   * Seeds the Title from the file name only while it's a single upload. */
  const addFiles = (picked: FileList | null): void => {
    if (!picked || picked.length === 0) return;
    const key = (f: { name: string; size: number }): string => `${f.name}:${f.size}`;
    const seen = new Set(files.map(key));
    const fresh: File[] = [];
    for (const f of Array.from(picked)) {
      if (seen.has(key(f))) continue;
      seen.add(key(f));
      fresh.push(f);
    }
    if (fresh.length === 0) return;
    const next = [...files, ...fresh];
    setFiles(next);
    setItems([...items, ...fresh.map<BulkFileState>((f) => ({ name: f.name, size: f.size, status: "pending" }))]);
    if (next.length === 1 && !title) setTitle(next[0]!.name.replace(/\.[^.]+$/, ""));
    setSummary(null);
    setError(null);
  };

  const removeAt = (index: number): void => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setItems((prev) => prev.filter((_, i) => i !== index));
    setSummary(null);
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (files.length === 0) {
      setError("Pick at least one file to upload.");
      return;
    }

    // Single file: honour the editable Title and redirect to the library —
    // the long-standing single-upload behaviour.
    if (files.length === 1) {
      setRunning(true);
      try {
        await uploadDoc({
          file: files[0]!,
          title,
          status,
          ...(categoryId ? { document_category_id: categoryId } : {}),
          // The pin puts it on the release shelf; the scope + mapping make the
          // release's audience its audience (a general document pinned to a
          // release would be listed under it yet open to everyone).
          ...(releaseId
            ? { default_release_id: releaseId, access_scope: "release" as const, release_ids: [Number(releaseId)] }
            : {}),
        });
        void qc.invalidateQueries({ queryKey: ["docs"] });
        navigate("/documentation");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed");
        setRunning(false);
      }
      return;
    }

    // Multiple files: one Document per file, all under the chosen category,
    // with per-file progress (title defaults to each file name).
    setRunning(true);
    setSummary(null);
    const res = await runBulkUpload(
      files,
      { status, ...(categoryId ? { categoryId } : {}), ...(releaseId ? { releaseId } : {}) },
      (i, patch) => setItems((prev) => prev.map((s, idx) => (idx === i ? applyBulkPatch(s, patch) : s))),
      undefined,
      // Re-run skips files already uploaded so retrying failures never dupes.
      (i) => items[i]?.status === "done",
    );
    setRunning(false);
    setSummary(res);
    void qc.invalidateQueries({ queryKey: ["docs"] });
  };

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Box component="header">
        <BackButton to="/documentation/manage/new" label="Back" sx={{ mb: 1.5 }} />
        <Typography component="h2" sx={{ mt: 0.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
          Upload documents
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
          Pick one or many files — each becomes its own document under the chosen
          category. Files land in DigitalOcean Spaces (
          <Box component="code" sx={{ borderRadius: 0.5, bgcolor: "surfaceSoft", px: 0.5, py: 0.25, fontFamily: "ui-monospace, monospace", fontSize: "11px" }}>
            xop-storage
          </Box>
          ) and the AI gateway picks them up for indexing.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={(e) => void submit(e)} sx={{ display: "grid", gap: 2.5 }}>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              sx={[
                {
                  borderRadius: 1,
                  border: 2,
                  borderStyle: "dashed",
                  p: 4,
                  textAlign: "center",
                  transition: "background-color 150ms, border-color 150ms",
                },
                dragOver
                  ? { borderColor: "primary.main", bgcolor: "action.selected" }
                  : { borderColor: "divider" },
              ]}
            >
              <Typography sx={{ fontSize: "1.5rem", color: "text.disabled" }}>↑</Typography>
              <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 500 }}>
                Drag files here{categoryName ? ` → ${categoryName}` : ""}
              </Typography>
              <Typography sx={{ mb: 1.5, fontSize: 12, color: "text.disabled" }}>
                PDF, Word, Excel, PowerPoint, image, video, code — drop several at
                once and one document is created per file.
              </Typography>
              <Box component="label" sx={{ display: "inline-flex" }}>
                <Box component="input" type="file" multiple sx={{ display: "none" }} onChange={(e: React.ChangeEvent<HTMLInputElement>) => addFiles(e.target.files)} />
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    height: 32,
                    cursor: "pointer",
                    alignItems: "center",
                    borderRadius: 1,
                    bgcolor: "surfaceSoft",
                    px: 1.5,
                    fontSize: 12,
                    fontWeight: 500,
                    "&:hover": { filter: "brightness(1.05)" },
                  }}
                >
                  {files.length ? "Add more files" : "Pick files"}
                </Box>
              </Box>
            </Box>

            {/* Single file: the familiar one-row card with an editable title. */}
            {single && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, borderRadius: 1, border: 1, borderColor: "divider", px: 1.5, py: 1 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500 }}>{files[0]!.name}</Box>
                    <Box sx={{ fontSize: "11px", color: "text.disabled" }}>
                      {files[0]!.type || "unknown"} · {formatBytes(files[0]!.size)}
                    </Box>
                  </Box>
                  <Button type="button" variant="text" color="inherit" size="small" onClick={() => removeAt(0)} disabled={running}>
                    Remove
                  </Button>
                </Box>
                <TextField
                  id="up-title"
                  label="Title"
                  placeholder="Defaults to the file name"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  helperText="Used when uploading a single file. With several files, each doc is titled from its file name."
                />
              </>
            )}

            {/* Many files: per-file progress + summary (no single title). */}
            {multi && <BulkFileList items={items} categoryName={categoryName} running={running} summary={summary} />}

            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { md: "repeat(2, 1fr)" } }}>
              <FolderSelect
                folders={folders}
                value={categoryId}
                onChange={setCategoryId}
                helperText={multi ? "Every file goes into this folder" : "The folder or sub-folder this document is filed in"}
              />
              <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as DocStatus)} fullWidth>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="in_review">In review</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </TextField>
            </Box>

            {error && (
              <Box sx={{ borderRadius: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.1), px: 1.5, py: 1, fontSize: 12, color: "error.main" }}>
                {error}
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: 1, borderColor: "divider", pt: 2 }}>
              <Chip label="DO bucket: xop-storage" size="small" sx={{ height: 22, borderRadius: 0.75, fontSize: 12, bgcolor: "surfaceSoft", color: "text.secondary" }} />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button component={RouterLink} to={summary ? "/documentation" : "/documentation/manage/new"} variant="text" color="inherit" size="small">
                  {summary ? "Done" : "Back"}
                </Button>
                <Button type="submit" variant="contained" size="small" disabled={files.length === 0 || running}>
                  {running
                    ? "Uploading…"
                    : multi
                      ? `Upload ${files.length} files`
                      : "Upload & create"}
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
